<template>
  <div class="u-containar-md" style="overflow: hidden">
    <HeadingLv2 text="イベントへの参加申込み" simple />
    <FlowVisitApplication
      v-if="$store.state.isLogin"
      :current="state.currentFlow"
      :isEvent="true"
    />
  </div>

  <router-view
    :state="state"
    :data="data"
    :err="errors"
    :notEnterdData="notEnterdData"
    @update-data="updateData"
    @update-flow="updateFlow"
    @update-landing-state="updateLandingState"
    @catch-error="catchError"
  ></router-view>
</template>

<script lang="ts">
import { defineComponent, PropType, reactive, ref } from 'vue'
import store from '@/store/'
import HeadingLv2 from '@/components/elements/HeadingLv2.vue'
import FlowVisitApplication from '@/components/block/FlowVisitApplication.vue'
import { axiosSetting } from '@/utils/axiosSetting'
import { EVENT_TYPE } from '@/utils/commonDataset'
import {
  getBirthdayPreviewHtml,
  getAddressPreviewHtml,
  getEventTerm
} from '@/utils/commonMethods' // eslint-disable-line
import {
  EventData,
  UserData,
  EventReserveFormState,
  EventReserveFormData,
  EventReserveFormNotEnterdData
} from '@/types/EventsReserve' // eslint-disable-line
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import 'dayjs/locale/ja'
dayjs.locale('ja')
dayjs.extend(isSameOrAfter)

export default defineComponent({
  name: 'EventReserve',

  components: {
    HeadingLv2,
    FlowVisitApplication
  },

  props: {
    eventData: {
      type: Object as PropType<EventData>,
      default: () => ({})
    },
    userData: {
      type: Object as PropType<UserData>,
      default: () => ({})
    }
  },

  beforeRouteEnter: async (to: any, from: any, next: any) => {
    // 遷移元のページをCookieに保存
    if (from.href) {
      document.cookie = `eventReserveFrom=${from.href}; max-age=3600; path=/`
    }

    if (!store.state.isLogin) {
      return next()
    }

    const promise = []
    let eventData = {}
    let userData = {}

    // ユーザー情報取得
    const ajax = axiosSetting('app', 'block')
    promise[0] = ajax.get('/api/v1/userInfo?is_all=1').then((res) => {
      return res.data
    })

    // イベント情報取得
    const ajaxCms = axiosSetting('cms', 'block')
    promise[1] = ajaxCms
      .get(`event/${to.params.id}`)
      .then((res) => {
        return res.data
      })
      .catch(() => {
        next('/404')
        return
      })

    await Promise.all(promise).then((data: any) => {
      for (let i = 0; i < data.length; i++) {
        if (i === 0) {
          userData = data[i] as UserData
        } else {
          const tempData = data[i] as EventData
          // イベント申し込みが別の場合は404にする
          if (tempData.acf.event_entry_link) {
            next('/404')
            break
          }
          // 現在の日付が開催期間を過ぎている場合は404にする
          const now = dayjs()
          const end = dayjs(tempData.acf.event_term.end)
          if (now.isAfter(end)) {
            next('/404')
            break
          }
          eventData = tempData
        }
      }
    })

    // イベントデータとユーザーデータを親コンポーネントに連携
    to.matched[0].props.default = {
      eventData: eventData,
      userData: userData
    }

    next()
  },

  setup(props) {
    const state: EventReserveFormState = reactive({
      isLandingInput: false,
      currentFlow: 1
    })

    // 申し込みフローの更新
    const updateFlow = (value: number): void => {
      state.currentFlow = value
    }

    // 入力ページからランディングしているか判定
    const updateLandingState = (value: boolean): void => {
      state.isLandingInput = value
    }

    // イベント種別を判別
    const getEvenType = (type: 'online' | 'offline'): number => {
      return type === 'online' ? EVENT_TYPE.online : EVENT_TYPE.offline
    }

    if (!store.state.isLogin) {
      return {
        state,
        data: {},
        notEnterdData: {},
        updateData: {},
        updateFlow,
        updateLandingState
      }
    }

    const memberType = props.userData.member_type === '医学生' ? '1' : '2'

    const data: EventReserveFormData = reactive({
      event: {
        name: {
          label: 'イベント名',
          content: props.eventData.title.rendered
        },
        date: {
          label: '開催日時',
          content: getEventTerm(
            props.eventData.acf.event_term.start,
            props.eventData.acf.event_term.end
          )
        },
        place: {
          label:
            getEvenType(props.eventData.acf.event_type.value) === 1
              ? '参加方法'
              : '会場',
          content:
            getEvenType(props.eventData.acf.event_type.value) === 1
              ? 'WEB会議ツールによるオンライン参加'
              : props.eventData.acf.event_place.name
        }
      },
      hiddenItem: {
        holding_type: {
          label: '開催種別',
          content: getEvenType(props.eventData.acf.event_type.value)
        }
      },
      user: {
        name: {
          label: '姓名（漢字）',
          content: props.userData.name_sei + props.userData.name_mei
        },
        name_kana: {
          label: '姓名（カナ）',
          content: props.userData.kana_sei + props.userData.kana_mei
        },
        gender: {
          label: '性別',
          content: props.userData.gender
        },
        birthday: {
          label: '生年月日',
          content: getBirthdayPreviewHtml(props.userData.birthday)
        },
        email: {
          label: 'メールアドレス',
          content: props.userData.email
        }
      }
    })

    if (
      props.userData.zip &&
      props.userData.prefecture &&
      props.userData.city &&
      props.userData.town
    ) {
      data.user.address = {
        label: '住所',
        content: getAddressPreviewHtml(
          props.userData.zip,
          props.userData.prefecture,
          props.userData.city,
          props.userData.town
        )
      }
    }

    data.user.tel = {
      label: '電話番号',
      content: props.userData.tel ? props.userData.tel : ''
    }

    if (props.userData.vacation_flg) {
      data.user.address_vacation = {
        label: '休暇中住所',
        content: '上記の住所と同じ'
      }
    } else if (
      props.userData.zip_vacation &&
      props.userData.prefecture_vacation &&
      props.userData.city_vacation &&
      props.userData.town_vacation
    ) {
      data.user.address_vacation = {
        label: '休暇中住所',
        content: getAddressPreviewHtml(
          props.userData.zip_vacation,
          props.userData.prefecture_vacation,
          props.userData.city_vacation,
          props.userData.town_vacation
        )
      }
    }

    data.user.school = {
      label: '大学名',
      content: props.userData.school_id
    }

    if (memberType === '1') {
      data.user.graduate_year = {
        label: '卒業時期（年）',
        content: props.userData.graduate_year
      }
    }
    if (memberType === '2') {
      data.user.work_place = {
        label: '現勤務先',
        content: props.userData.work_place
      }

      data.user.licensing_year = {
        label: '医師免許取得年',
        content: props.userData.licensing_year
      }
    }

    const notEnterdData: EventReserveFormNotEnterdData = reactive({
      zip: props.userData.zip || '',
      prefecture: props.userData.prefecture || '',
      city: props.userData.city || '',
      town: props.userData.town || '',
      tel: props.userData.tel || '',
      zip_vacation: props.userData.zip_vacation || '',
      prefecture_vacation: props.userData.prefecture_vacation || '',
      city_vacation: props.userData.city_vacation || '',
      town_vacation: props.userData.town_vacation || '',
      vacation_flg: props.userData.vacation_flg
    })

    const updateData = (name: string, value: string): void => {
      notEnterdData[name] = value
    }

    const errors = ref({})
    const catchError = (errMsg: any): void => {
      errors.value = errMsg
    }

    return {
      state,
      data,
      notEnterdData,
      updateData,
      updateFlow,
      updateLandingState,
      errors,
      catchError
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;
</style>
